import React from 'react';
import { Mail, Settings, CheckCircle, ExternalLink } from 'lucide-react';

const EmailJSSetup: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <Mail className="h-12 w-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          EmailJS Setup Instructions
        </h2>
        <p className="text-lg text-gray-600">
          Follow these steps to configure email notifications for your contact forms
        </p>
      </div>

      <div className="space-y-8">
        {/* Step 1 */}
        <div className="border-l-4 border-blue-500 pl-6">
          <div className="flex items-center mb-3">
            <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
              1
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Create EmailJS Account</h3>
          </div>
          <p className="text-gray-600 mb-3">
            Sign up for a free account at EmailJS to get started with email integration.
          </p>
          <a
            href="https://www.emailjs.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            Visit EmailJS <ExternalLink className="h-4 w-4 ml-1" />
          </a>
        </div>

        {/* Step 2 */}
        <div className="border-l-4 border-green-500 pl-6">
          <div className="flex items-center mb-3">
            <div className="bg-green-100 text-green-600 rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
              2
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Add Email Service</h3>
          </div>
          <p className="text-gray-600 mb-3">
            Connect your email provider (Gmail, Outlook, etc.) in the EmailJS dashboard.
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Go to Email Services in your EmailJS dashboard</li>
            <li>Click "Add New Service"</li>
            <li>Choose your email provider (Gmail recommended)</li>
            <li>Follow the authentication steps</li>
          </ul>
        </div>

        {/* Step 3 */}
        <div className="border-l-4 border-purple-500 pl-6">
          <div className="flex items-center mb-3">
            <div className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
              3
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Create Email Template</h3>
          </div>
          <p className="text-gray-600 mb-3">
            Create a template for the contact form notifications.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Template Variables to Include:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div><code>{'{{from_name}}'}</code> - Contact's name</div>
              <div><code>{'{{from_email}}'}</code> - Contact's email</div>
              <div><code>{'{{company}}'}</code> - Company name</div>
              <div><code>{'{{phone}}'}</code> - Phone number</div>
              <div><code>{'{{message}}'}</code> - Message content</div>
              <div><code>{'{{source}}'}</code> - Form source</div>
            </div>
          </div>
        </div>

        {/* Step 4 */}
        <div className="border-l-4 border-orange-500 pl-6">
          <div className="flex items-center mb-3">
            <div className="bg-orange-100 text-orange-600 rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">
              4
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Update Configuration</h3>
          </div>
          <p className="text-gray-600 mb-3">
            Update the EmailJS configuration in your code with your credentials.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              Edit <code>src/lib/emailjs.ts</code> and replace:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li><code>your_service_id</code> - Your EmailJS service ID</li>
              <li><code>your_template_id</code> - Your EmailJS template ID</li>
              <li><code>your_public_key</code> - Your EmailJS public key</li>
            </ul>
          </div>
        </div>

        {/* Sample Template */}
        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Sample Email Template
          </h3>
          <div className="bg-white p-4 rounded border text-sm">
            <div className="mb-2"><strong>Subject:</strong> New Contact Form Submission from {'{{from_name}}'}</div>
            <div className="whitespace-pre-line text-gray-700">
{`Hello HARMITON WebTech Team,

You have received a new contact form submission:

Name: {{from_name}}
Email: {{from_email}}
Company: {{company}}
Phone: {{phone}}
Source: {{source}}

Message:
{{message}}

Please respond to this inquiry promptly.

Best regards,
Website Contact Form`}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-green-50 rounded-lg">
        <div className="flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-800 font-medium">
            Once configured, you'll receive email notifications for all contact form submissions!
          </span>
        </div>
      </div>
    </div>
  );
};

export default EmailJSSetup;